import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column } from '../../../components/AsyncTable';
import NewRegisterBtn from '../../../components/Buttons/NewRegisterBtn';
import { ActionButtonsContainer } from '../../../components/TablePaginator/styles';
import { DeleteIcon, EditIcon, ViewDetailsIcon } from '../../../icons';
import { formAPI } from '../../../services/requests';
import ContentContainer from '../../../templates/Content/ContentContainer';
import { Form } from '../../../types';
import getGenericRequestPageForAsyncTable from '../../../utils/TablesFunctions';
import { formOpeningLocations } from '../../../utils/fixedData';
import { isDateTodayOrPast } from '../../../utils/helpers';
import FormModal from './Components/ModalCreateAndEditForm/FormModal';
import DeleteFormModal from './Components/ModalDelete';

const FormsJSX = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [compData, setCompData] = useState<Form[]>([]);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);

  const [isModalDeleteVisible, setIsModalDeleteVisible] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [updateTable, setUpdateTable] = useState(0);

  const handleTableEditBtn = (row: Form) => {
    setSelectedForm(row);
    setIsFormModalVisible(true);
  };

  const handleTableDeleteBtn = (row: Form) => {
    setSelectedForm(row);
    setIsModalDeleteVisible(true);
  };

  const actionsButtons = (row: Form) => (
    <ActionButtonsContainer type={4}>
      {!isDateTodayOrPast(row.initialDate) ? (
        <>
          <EditIcon title={t('edit')} onClick={() => handleTableEditBtn(row)} />
          <DeleteIcon title={t('delete')} onClick={() => handleTableDeleteBtn(row)} />
        </>
      ) : (
        <ViewDetailsIcon title={t('toView')} onClick={() => handleTableEditBtn(row)} />
      )}
    </ActionButtonsContainer>
  );

  const handleAudienceColumn = (row: Form) => <p>{row.audience?.description}</p>;

  const handleRoutineColumn = (row: Form) => <p>{formOpeningLocations()[row.openingLocal]}</p>;

  const columns: Column<Form>[] = [
    {
      label: t('description') as string,
      accessor: 'description',
    },
    {
      label: t('initialDate') as string,
      accessor: 'initialDate',
    },
    {
      label: t('finalDate') as string,
      accessor: 'finalDate',
    },
    {
      label: t('routine') as string,
      Cell: handleRoutineColumn,
    },
    {
      label: t('audience') as string,
      Cell: handleAudienceColumn,
    },
    {
      Cell: actionsButtons,
    },
  ];

  const handleOnCloseModals = () => {
    setIsFormModalVisible(false);
    setIsModalDeleteVisible(false);
    setSelectedForm(null);
  };

  const handleCallbackModals = () => {
    handleOnCloseModals();
    setUpdateTable(updateTable + 1);
  };

  return (
    <ContentContainer title={t('forms')}>
      <NewRegisterBtn
        onClick={() => setIsFormModalVisible(true)}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      {isFormModalVisible && (
        <FormModal
          closeModal={() => handleOnCloseModals()}
          isVisible={isFormModalVisible}
          saveCallback={() => handleCallbackModals()}
          {...(selectedForm && { selectedForm })}
        />
      )}

      {isModalDeleteVisible && selectedForm && (
        <DeleteFormModal
          selectedItem={selectedForm as Form}
          closeModal={() => handleOnCloseModals()}
          reqCallback={() => handleCallbackModals()}
        />
      )}

      <AsyncTable
        tableName={t('forms')}
        columns={columns}
        value={compData}
        onChange={setCompData}
        requestPage={getGenericRequestPageForAsyncTable(formAPI)}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />
    </ContentContainer>
  );
};

export default FormsJSX;
